/**
 * Routing for the shop. No router library: the shop has a dozen routes, and the History API is
 * enough. Everything a list page needs to survive a refresh, the Back button or a shared link
 * (page, sort, per-page, filters, view) lives in the URL query, carried here as `qs`.
 */
import { useCallback, useEffect, useState } from "react";
import { matchShopPath, safeDecode, splitDeptParam } from "../../../src/shop-routes";
import { isShopHost } from "../../../src/shop-hosts";

export type Route =
  | { name: "home" }
  | { name: "category"; handle: string; qs?: string }
  | { name: "dept"; id: string; slug: string; qs?: string }
  | { name: "search"; qs?: string }
  | { name: "clearance"; qs?: string }
  | { name: "campaigns" }
  | { name: "campaign"; slug: string }
  | { name: "bundles"; theme?: string }
  | { name: "bundle"; slug: string }
  | { name: "guides" }
  | { name: "guide"; slug: string }
  | { name: "collections" }
  | { name: "faq" }
  | { name: "contact" }
  | { name: "about" }
  | { name: "product"; handle: string }
  | { name: "basket" }
  | { name: "checkout" }
  | { name: "account"; tab?: "in" | "up" | "forgot" | "claim" }
  | { name: "verify"; token: string }
  | { name: "reset"; token: string }
  | { name: "welcome"; token: string }
  | { name: "order"; id: string; ref: string; cancelled: boolean }
  | { name: "orders"; id?: string }
  | { name: "track" }
  | { name: "review"; token: string }
  | { name: "policy"; handle: string }
  | { name: "notfound" };

/**
 * ── The shop is mounted two ways, and every path this file writes has to know which ──────────
 *
 * On `shop.*` the shop IS the whole origin, so its own paths are root-relative: `/c/kitchen`.
 * Until that subdomain exists (see the DNS to-do), it is also reached at `/shop` on the SAME
 * host as the Relay dashboard — and there, `/c/kitchen` is the DASHBOARD's own root, not the
 * shop's. `pushState(null, "", "/c/kitchen")` silently dropped the `/shop` prefix the moment
 * anyone clicked a category or product link, and refreshing from there landed back on Relay —
 * because as far as the server is concerned, `/c/kitchen` was never a shop path to begin with.
 *
 * `MOUNT` is read once, from the hostname, which cannot change without a full reload — so it is
 * safe as a module-level constant rather than something recomputed per navigation.
 * "Is this a shop host" is the SAME function the Worker uses (`src/shop-hosts.ts`): `shop.*` and
 * www.zsmerch.co.za own the whole origin.
 */
export const MOUNT = isShopHost(window.location.hostname) ? "" : "/shop";

const cleanQs = (qs: string | undefined) => (qs ? qs.replace(/^\?/, "") : "");

/**
 * The path → route decision, from the SAME table the Worker's front door uses
 * (`worker/src/shop-routes.ts`), so a page that works here never 404s on a refresh and a path the
 * server 404s is a not-found here too. Never throws: a malformed `%` escape (a mangled link from a
 * chat app) is the not-found page, not a blank shop.
 */
export function readRoute(): Route {
  const raw = window.location.pathname;
  const stripped = MOUNT && (raw === MOUNT || raw.startsWith(`${MOUNT}/`)) ? raw.slice(MOUNT.length) : raw;
  return routeForPath(stripped, window.location.search);
}

/**
 * A path INSIDE the shop (no `/shop` mount prefix) plus its query → the route. The one decision
 * every caller shares — `readRoute` for the address bar, `HeroCarousel` for a slide's
 * server-written CTA path — so a route added to `shop-routes.ts` cannot work in one and silently
 * fall back to home in the other. The `never` check below fails the build until it is handled.
 */
export function routeForPath(path: string, search = ""): Route {
  try {
    const m = matchShopPath(path);
    if (!m) return { name: "notfound" };
    const param = m.param === null ? null : safeDecode(m.param);
    if (m.param !== null && param === null) return { name: "notfound" };
    const q = new URLSearchParams(search);
    const qs = cleanQs(search);
    switch (m.def.name) {
      case "home": return { name: "home" };
      case "category": return { name: "category", handle: param!, qs };
      case "dept": return { name: "dept", ...splitDeptParam(param!), qs };
      case "search": return { name: "search", qs };
      case "clearance": return { name: "clearance", qs };
      case "campaign": return param !== null ? { name: "campaign", slug: param } : { name: "campaigns" };
      case "bundles": return param !== null ? { name: "bundle", slug: param } : { name: "bundles", theme: q.get("theme") ?? undefined };
      case "guides": return param !== null ? { name: "guide", slug: param } : { name: "guides" };
      case "collections": return { name: "collections" };
      case "help": return { name: "faq" };
      case "faq": return { name: "faq" };
      case "contact": return { name: "contact" };
      case "about": return { name: "about" };
      case "product": return { name: "product", handle: param! };
      case "basket": return { name: "basket" };
      case "checkout": return { name: "checkout" };
      case "account": return { name: "account", tab: q.get("tab") === "up" ? "up" : q.get("tab") === "forgot" ? "forgot" : q.get("tab") === "claim" ? "claim" : "in" };
      case "signup": return { name: "account", tab: "up" };
      case "verify": return { name: "verify", token: q.get("token") ?? "" };
      case "reset": return { name: "reset", token: q.get("token") ?? "" };
      case "welcome": return { name: "welcome", token: q.get("token") ?? "" };
      case "order": return { name: "order", id: param!, ref: q.get("key") ?? q.get("ref") ?? "", cancelled: q.get("cancelled") === "1" };
      case "orders": return param !== null ? { name: "orders", id: param } : { name: "orders" };
      case "track": return { name: "track" };
      case "review": return { name: "review", token: q.get("token") ?? "" };
      case "policy": return { name: "policy", handle: param! };
      default: {
        const unhandled: never = m.def.name;
        return unhandled;
      }
    }
  } catch {
    /* fall through: anything unexpected is a not-found, never a crash */
  }
  return { name: "notfound" };
}

export function pathFor(r: Route): string {
  const withQs = (base: string, qs?: string) => (cleanQs(qs) ? `${base}?${cleanQs(qs)}` : base);
  const p = (() => {
    switch (r.name) {
      case "home": return "/";
      case "category": return withQs(`/c/${encodeURIComponent(r.handle)}`, r.qs);
      case "dept": return withQs(`/d/${encodeURIComponent(r.slug ? `${r.id}-${r.slug}` : r.id)}`, r.qs);
      case "search": return withQs("/search", r.qs);
      case "clearance": return withQs("/clearance", r.qs);
      case "campaigns": return "/s";
      case "campaign": return `/s/${encodeURIComponent(r.slug)}`;
      case "bundles": return r.theme ? `/bundles?theme=${encodeURIComponent(r.theme)}` : "/bundles";
      case "bundle": return `/bundles/${encodeURIComponent(r.slug)}`;
      case "guides": return "/guides";
      case "guide": return `/guides/${encodeURIComponent(r.slug)}`;
      case "collections": return "/collections";
      case "faq": return "/faq";
      case "contact": return "/contact";
      case "about": return "/about";
      case "product": return `/p/${encodeURIComponent(r.handle)}`;
      case "basket": return "/basket";
      case "checkout": return "/checkout";
      case "account": return r.tab === "up" ? "/signup" : r.tab === "forgot" ? "/account?tab=forgot" : r.tab === "claim" ? "/account?tab=claim" : "/account";
      case "verify": return `/verify?token=${encodeURIComponent(r.token)}`;
      case "reset": return `/reset?token=${encodeURIComponent(r.token)}`;
      case "welcome": return `/welcome?token=${encodeURIComponent(r.token)}`;
      case "order": return `/order/${encodeURIComponent(r.id)}?key=${encodeURIComponent(r.ref)}`;
      case "orders": return r.id ? `/account/orders/${encodeURIComponent(r.id)}` : "/account/orders";
      case "track": return "/track";
      case "review": return `/review?token=${encodeURIComponent(r.token)}`;
      case "policy": return `/policies/${encodeURIComponent(r.handle)}`;
      /** Not-found has no address of its own; leaving it means going somewhere real. */
      case "notfound": return "/";
    }
  })();
  /** ⚠️ `/shop` + `/` must collapse to `/shop`, not `/shop/` — a trailing slash on the mount's own
      home is a different path from `/shop` as far as the server-side rewrite below is concerned. */
  return p === "/" ? MOUNT || "/" : `${MOUNT}${p}`;
}

/**
 * A discount code arriving on ANY page (`?code=SPRING10`), not only checkout — a link shared from
 * the Discounts page might point at the home page or a product, and the code should still reach
 * the customer whenever they eventually get to checkout. Captured once per landing into
 * sessionStorage; Checkout consumes it on its own mount (see PENDING_CODE_KEY there).
 */
export const PENDING_CODE_KEY = "zs_pending_discount_code";
function capturePendingDiscountCode(): void {
  const code = new URLSearchParams(window.location.search).get("code");
  if (!code) return;
  try {
    sessionStorage.setItem(PENDING_CODE_KEY, code.toUpperCase());
  } catch {
    // A private window or blocked storage just means the code has to be typed — not a broken page.
  }
}

export type GoFn = (r: Route, opts?: { replace?: boolean; keepScroll?: boolean }) => void;

export function useRoute(): [Route, GoFn] {
  const [route, setRoute] = useState<Route>(readRoute);
  useEffect(() => {
    capturePendingDiscountCode();
    const onPop = () => setRoute(readRoute());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const go = useCallback<GoFn>((r, opts) => {
    if (opts?.replace) window.history.replaceState({}, "", pathFor(r));
    else window.history.pushState({}, "", pathFor(r));
    setRoute(r);
    if (!opts?.keepScroll) window.scrollTo(0, 0);
  }, []);
  return [route, go];
}

/** A link that navigates in-app but is still a real link for the keyboard, the crawler and the middle click. */
export function Go({ to, go, className, style, children, ariaCurrent, onNavigate, id, role, tabIndex, onFocus }: {
  to: Route; go: GoFn; className?: string; style?: React.CSSProperties; children: React.ReactNode; ariaCurrent?: "page";
  onNavigate?: () => void; id?: string; role?: string; tabIndex?: number; onFocus?: () => void;
}) {
  return (
    <a
      id={id}
      role={role}
      tabIndex={tabIndex}
      onFocus={onFocus}
      href={pathFor(to)}
      className={className}
      style={style}
      aria-current={ariaCurrent}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        onNavigate?.();
        go(to);
      }}
    >
      {children}
    </a>
  );
}

/** Returns a new query string with `changes` applied; a `null` value removes the key. */
export function qsSet(qs: string | undefined, changes: Record<string, string | string[] | null>): string {
  const p = new URLSearchParams(cleanQs(qs));
  for (const [k, v] of Object.entries(changes)) {
    p.delete(k);
    if (v === null) continue;
    if (Array.isArray(v)) v.forEach((x) => p.append(k, x));
    else p.set(k, v);
  }
  return p.toString();
}
